// src/pages/MarketplaceIntegration/schedulerService.js

// This service handles scheduled marketplace synchronization tasks
// In a real implementation, this would use Firebase Functions or a similar backend service

class SchedulerService {
  constructor() {
    this.jobs = new Map();
    this.isRunning = false;
  }

  // Start the scheduler
  start() {
    if (this.isRunning) return;
    
    this.isRunning = true;
    console.log('Scheduler service started');
    
    // In a real implementation, this would be handled by a backend service
    // For demo purposes, we'll simulate with setInterval
  }

  // Stop the scheduler
  stop() {
    this.isRunning = false;
    console.log('Scheduler service stopped');
    
    // Clear all intervals
    this.jobs.forEach(job => {
      if (job.intervalId) {
        clearInterval(job.intervalId);
      }
    });
    this.jobs.clear();
  }

  // Schedule a job to run at specified intervals
  scheduleJob(jobId, intervalMinutes, callback, params = {}) {
    // Clear existing job if it exists
    if (this.jobs.has(jobId)) {
      this.cancelJob(jobId);
    }

    // Convert minutes to milliseconds
    const intervalMs = intervalMinutes * 60 * 1000;
    
    // Create the job
    const job = {
      id: jobId,
      interval: intervalMinutes,
      callback,
      params,
      intervalId: null,
      lastRun: null,
      nextRun: new Date(Date.now() + intervalMs)
    };

    // Start the interval
    job.intervalId = setInterval(async () => {
      try {
        console.log(`Running scheduled job: ${jobId}`);
        job.lastRun = new Date();
        await callback(params);
        job.nextRun = new Date(Date.now() + intervalMs);
      } catch (error) {
        console.error(`Error in scheduled job ${jobId}:`, error);
      }
    }, intervalMs);

    // Store the job
    this.jobs.set(jobId, job);
    
    console.log(`Scheduled job ${jobId} to run every ${intervalMinutes} minutes`);
    return job;
  }

  // Cancel a scheduled job
  cancelJob(jobId) {
    const job = this.jobs.get(jobId);
    if (job && job.intervalId) {
      clearInterval(job.intervalId);
      this.jobs.delete(jobId);
      console.log(`Cancelled scheduled job: ${jobId}`);
      return true;
    }
    return false;
  }

  // Get all scheduled jobs
  getScheduledJobs() {
    return Array.from(this.jobs.values()).map(job => ({
      id: job.id,
      interval: job.interval,
      lastRun: job.lastRun,
      nextRun: job.nextRun
    }));
  }

  // Update job interval
  updateJobInterval(jobId, newIntervalMinutes) {
    const job = this.jobs.get(jobId);
    if (!job) return false;
    
    // Cancel the existing job
    this.cancelJob(jobId);
    
    // Reschedule with new interval
    this.scheduleJob(jobId, newIntervalMinutes, job.callback, job.params);
    
    console.log(`Updated job ${jobId} interval to ${newIntervalMinutes} minutes`);
    return true;
  }

  // Run a job immediately
  async runJobImmediately(jobId) {
    const job = this.jobs.get(jobId);
    if (!job) return false;
    
    try {
      console.log(`Running job immediately: ${jobId}`);
      await job.callback(job.params);
      job.lastRun = new Date();
      return true;
    } catch (error) {
      console.error(`Error running job ${jobId} immediately:`, error);
      return false;
    }
  }
}

// Export singleton instance
const schedulerService = new SchedulerService();
export default schedulerService;